// src/pages/gt3x/Startfreigabe.jsx
import { useEffect, useMemo, useState } from "react";
import { fetchSheetViaProxy } from "../../lib/sheets";
import RaceSessionSelect from "../../components/RaceSessionSelect";
import { getAutoRaceFromSchedule } from "../../lib/autoRaceFromSchedule";

const RANGE = "Abmeldung!A1:Z400";
const SCHEDULE_RANGE = "Schedule!A1:D";

/* -------------------------------------------------
   SimpleTable mit:
   - gelbem Background für WARTELISTE
   - schwarzem Text für WARTELISTE
   - JA = grün, NEIN = rot
--------------------------------------------------- */
function SimpleTable({ header, rows, emptyMessage }) {
    if (!header || header.length === 0) return null;

    const hasRows = Array.isArray(rows) && rows.length > 0;

    return (
        <div className="card overview-card">
            <div className="fancyTable startfreigabe-table-wrap">
                <table className="tbl tbl-start">
                    <thead>
                    <tr>
                        {header.map((h, i) => (
                            <th key={i}>{h}</th>
                        ))}
                    </tr>
                    </thead>

                    <tbody>
                    {hasRows ? (
                        rows.map((r, ri) => {
                            const firstCell = r[0];
                            const isWaitlist =
                                String(firstCell).trim().toLowerCase() === "warteliste";

                            return (
                                <tr
                                    key={ri}
                                    className={isWaitlist ? "waitlist-row" : ""}
                                    style={{
                                        backgroundColor: isWaitlist ? "#e4fe3c" : "transparent",
                                        color: isWaitlist ? "#232F2D" : "inherit",
                                    }}
                                >
                                    {r.map((cell, ci) => {
                                        const raw = cell ?? "";
                                        const val = String(raw).trim().toLowerCase();

                                        let color = isWaitlist ? "#232F2D" : "inherit";
                                        if (!isWaitlist) {
                                            if (val === "ja") color = "#69fb90";
                                            if (val === "nein") color = "#ff8c8c";
                                            if (val === "abmeldung") color = "#ff8c8c";
                                        }

                                        return (
                                            <td key={ci} style={{ color }}>
                                                {raw}
                                            </td>
                                        );
                                    })}
                                </tr>
                            );
                        })
                    ) : (
                        <tr>
                            <td colSpan={header.length} className="align-left text-dim">
                                {emptyMessage || "Keine Einträge für dieses Rennen vorhanden."}
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default function Startfreigabe() {
    const [race, setRace] = useState(null);
    const [raceTouched, setRaceTouched] = useState(false);

    const [header, setHeader] = useState([]);
    const [allRows, setAllRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // ✅ AUTO-DEFAULT: BriefingFrom (Spalte C) -> mode "briefing"
    useEffect(() => {
        (async () => {
            try {
                const auto = await getAutoRaceFromSchedule(
                    fetchSheetViaProxy,
                    SCHEDULE_RANGE,
                    "briefing"
                );
                if (!raceTouched) setRace(auto);
            } catch (e) {
                console.warn("Schedule auto-race failed (startfreigabe)", e);
                if (!raceTouched) setRace(1);
            }
        })();
    }, [raceTouched]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            setErr("");

            try {
                const res = await fetchSheetViaProxy({ range: RANGE });

                let h = [];
                let rowsMatrix = [];

                if (Array.isArray(res?.header) && Array.isArray(res?.rows)) {
                    h = res.header;
                    if (Array.isArray(res.rows[0])) rowsMatrix = res.rows;
                    else rowsMatrix = res.rows.map((obj) => h.map((c) => obj[c]));
                } else if (Array.isArray(res?.values) && res.values.length > 0) {
                    const [head, ...body] = res.values;
                    h = head;
                    rowsMatrix = body;
                }

                setHeader(h);
                setAllRows(rowsMatrix);
            } catch (e) {
                setErr("Fehler beim Laden der Startfreigabe-Daten.");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const { tableHeader, tableRows } = useMemo(() => {
        if (!header.length || !allRows.length || !race) {
            return { tableHeader: [], tableRows: [] };
        }

        const nameIdx = 1;
        const colStart = 2 + (race - 1) * 2;
        const colEnd = colStart + 1;

        if (colEnd >= header.length) return { tableHeader: [], tableRows: [] };

        const tableHeader = [
            header[nameIdx] ?? "Fahrer",
            header[colStart] ?? `Abm. R${race}`,
            header[colEnd] ?? `Start R${race}`,
        ];

        const tableRows = allRows
            .filter((row) => row?.[nameIdx] && row[nameIdx] !== "")
            .map((row) => [row[nameIdx] ?? "", row[colStart] ?? "", row[colEnd] ?? ""]);

        return { tableHeader, tableRows };
    }, [header, allRows, race]);

    const hasData = tableRows.length > 0;

    return (
        <>
            <RaceSessionSelect
                valueRace={race}
                onRaceChange={setRace}
                onUserRaceChange={() => setRaceTouched(true)}
                autoSelectFirst={false}
                showSession={false}
            />

            <div className="card overview-card-top">
                <h1>Startfreigabe – GT3 Xperience</h1>
                <p className="muted">
                    Jeder regulär angemeldete Fahrer ist grundsätzlich zu jedem Rennen startberechtigt
                    (ausgenommen: Strafsperre)
                    <br />
                    <br />
                    Fahrer von der Warteliste müssen darauf warten, dass sich Stammfahrer abmelden.
                    Dies kann hier eingesehen werden.
                </p>
            </div>

            {err && (
                <div className="card start-card-top">
                    <p>{err}</p>
                </div>
            )}

            {loading && (
                <div className="card start-card-top">
                    <p>Lade Daten…</p>
                </div>
            )}

            {!loading && !err && (
                <SimpleTable
                    header={tableHeader}
                    rows={tableRows}
                    emptyMessage={
                        hasData ? undefined : "Für dieses Rennen liegen noch keine Einträge vor."
                    }
                />
            )}
        </>
    );
}
